import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Authentticatevalues, ROUTES, STORAGE_KEYS } from '@/constants';
import { queryClient } from '@/lib/queryClient';
import { UpdateUserLogAsync } from '@/service/AuthenticationService';
import { disconnectSignalR } from '@/service/signalRService';
import {
  store,
  useAppDispatch,
  useAppSelector,
  selectAccessToken,
  selectConfig,
  clearAuth,
  resetStartup,
} from '@/store';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
const STORAGE_WRITE_THROTTLE_MS = 30_000;

function readStoredActivityTime(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.lastActivityTime);
    if (raw) {
      const stored = JSON.parse(raw) as number;
      if (typeof stored === 'number' && !isNaN(stored)) return stored;
    }
  } catch {
    /* ignore */
  }
  return Date.now();
}

interface SessionManagerState {
  showWarning: boolean;
  countdown: number;
  onContinue: () => void;
}

/**
 * Matches Xamarin MainActivity.cs inactivity flow:
 * - APP_INACTIVITY_START_TIME: outer gate (min idle before checking)
 * - APP_AUTO_LOGOUT_TIME: trigger threshold (show popup)
 * - APP_AUTO_LOGOUT_TIMEOUT: popup countdown duration
 */
export function useSessionManager(): SessionManagerState {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const accessToken = useAppSelector(selectAccessToken);
  const config = useAppSelector(selectConfig);

  const inactivityStartTime = parseFloat(config?.settings.APP_INACTIVITY_START_TIME ?? '0');
  const autoLogoutTime = parseFloat(config?.settings.APP_AUTO_LOGOUT_TIME ?? '0');
  const autoLogoutTimeout = parseFloat(config?.settings.APP_AUTO_LOGOUT_TIMEOUT ?? '0');

  const lastActivityRef = useRef(readStoredActivityTime());
  const warningActiveRef = useRef(false);
  const logoutFiredRef = useRef(false);
  const lastStorageWriteRef = useRef(0);
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const isOnLogoutPage = location.pathname === ROUTES.logoutSummary;

  const performLogout = useCallback(async () => {
    const state = store.getState();
    const userId = state.user.user?._id ?? '';
    const plantId = state.user.plant?._id ?? '';
    const sid = state.user.sessionId ?? '';

    try {
      await UpdateUserLogAsync({
        user: userId,
        status: Authentticatevalues.UserStatusAutoLogout,
        activity_date_time: new Date().toISOString(),
        session_id: sid,
        plant_id: plantId,
      });
    } catch {
      // Proceed with logout even if logging fails
    }

    disconnectSignalR();

    localStorage.removeItem(STORAGE_KEYS.user);
    localStorage.removeItem(STORAGE_KEYS.sessionId);
    localStorage.removeItem(STORAGE_KEYS.authToken);
    localStorage.removeItem(STORAGE_KEYS.tokenExpiry);
    localStorage.removeItem(STORAGE_KEYS.plant);
    localStorage.removeItem('yms_verify_data');
    sessionStorage.removeItem(STORAGE_KEYS.refreshToken);
    sessionStorage.removeItem(STORAGE_KEYS.otpMobile);
    sessionStorage.removeItem(STORAGE_KEYS.otpCountryCode);
    localStorage.removeItem(STORAGE_KEYS.breakModeActive);
    localStorage.removeItem(STORAGE_KEYS.breakModeFrom);
    localStorage.removeItem(STORAGE_KEYS.breakTimerStart);
    localStorage.removeItem(STORAGE_KEYS.shuttleModeActive);
    localStorage.removeItem(STORAGE_KEYS.shuttleTimerStart);
    localStorage.removeItem(STORAGE_KEYS.shuttleTimerPausedElapsed);
    localStorage.removeItem(STORAGE_KEYS.offlineUpdatedTaskList);
    localStorage.removeItem(STORAGE_KEYS.offlineMyTaskList);
    localStorage.removeItem(STORAGE_KEYS.inProgressTask);
    localStorage.removeItem(STORAGE_KEYS.offlineUpcomingTask);
    localStorage.removeItem(STORAGE_KEYS.offlineCompletedTask);
    localStorage.removeItem(STORAGE_KEYS.loginDateTime);
    localStorage.removeItem(STORAGE_KEYS.lastActivityTime);

    void queryClient.cancelQueries();
    queryClient.clear();
    dispatch(clearAuth());
    dispatch(resetStartup());
    navigate(ROUTES.login, { replace: true });
  }, [dispatch, navigate]);

  // Reset idle timer on user interaction (only when warning is NOT active)
  const resetActivity = useCallback(() => {
    if (!warningActiveRef.current) {
      lastActivityRef.current = Date.now();
    }
  }, []);

  // Register interaction event listeners
  useEffect(() => {
    if (!accessToken || autoLogoutTime <= 0) return;

    for (const event of ACTIVITY_EVENTS) {
      document.addEventListener(event, resetActivity, { passive: true });
    }
    lastActivityRef.current = Date.now();
    try {
      localStorage.setItem(STORAGE_KEYS.lastActivityTime, JSON.stringify(Date.now()));
    } catch {
      /* ignore */
    }

    return () => {
      for (const event of ACTIVITY_EVENTS) {
        document.removeEventListener(event, resetActivity);
      }
    };
  }, [accessToken, autoLogoutTime, resetActivity]);

  // Main idle-check interval
  useEffect(() => {
    if (!accessToken || autoLogoutTime <= 0 || isOnLogoutPage) return;

    const interval = setInterval(() => {
      if (warningActiveRef.current) return;

      // Throttled persist of last activity time for cold-start inactivity check
      const now = Date.now();
      if (now - lastStorageWriteRef.current >= STORAGE_WRITE_THROTTLE_MS) {
        lastStorageWriteRef.current = now;
        try {
          localStorage.setItem(
            STORAGE_KEYS.lastActivityTime,
            JSON.stringify(lastActivityRef.current)
          );
        } catch {
          /* ignore */
        }
      }

      const idleSeconds = (now - lastActivityRef.current) / 1000;

      if (idleSeconds >= inactivityStartTime && idleSeconds >= autoLogoutTime) {
        warningActiveRef.current = true;
        setShowWarning(true);
        setCountdown(Math.ceil(autoLogoutTimeout > 0 ? autoLogoutTimeout : 60));
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [accessToken, inactivityStartTime, autoLogoutTime, autoLogoutTimeout, isOnLogoutPage]);

  // Countdown timer when warning is active
  useEffect(() => {
    if (!showWarning) return;

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) return 0;
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [showWarning]);

  // Trigger logout when countdown reaches 0
  useEffect(() => {
    if (!showWarning || countdown !== 0) return;
    if (logoutFiredRef.current) return;
    logoutFiredRef.current = true;
    void performLogout();
  }, [showWarning, countdown, performLogout]);

  const handleContinue = useCallback(() => {
    warningActiveRef.current = false;
    logoutFiredRef.current = false;
    lastActivityRef.current = Date.now();
    try {
      localStorage.setItem(STORAGE_KEYS.lastActivityTime, JSON.stringify(Date.now()));
    } catch {
      /* ignore */
    }
    setShowWarning(false);
    setCountdown(0);
  }, []);

  return { showWarning, countdown, onContinue: handleContinue };
}
